import React, { useRef, useEffect } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import 'quill/modules/history';
import { ReactComponent as Undo } from '../../assets/svg/undo.svg';
import { ReactComponent as Redo } from '../../assets/svg/redo.svg';

interface Props {
    name?: string;
    value?: string;
    onChange(content: any): void;
    styles?: any;
}

export const QuillEditor: React.FC<Props> = (props) => {
    const editorRef = useRef<any>(null);
    const quillRef = useRef<any>(null);
    const toolbarRef = useRef<any>(null);

    useEffect(() => {
        const Font = Quill.import('formats/font') as any;
        Font.whitelist = ['arial', 'courier', 'comic-sans-ms', 'helvetica', 'open-sans', 'tahoma', 'verdana', 'sans-serif', 'serif', 'monospace'];
        Quill.register(Font, true);
        const Size = Quill.import('formats/size') as any;
        Size.whitelist = ['small', 'normal', 'large'];
        Quill.register(Size, true);

        quillRef.current = new Quill(editorRef.current, {
            theme: 'snow',
            modules: {
                toolbar: {
                    container: toolbarRef.current,
                    handlers: {
                        'undo': undo,
                        'redo': redo
                    }
                },
                history: {
                    delay: 1000,
                    maxStack: 500,
                    userOnly: true
                },
                clipboard: {
                    matchVisual: false,
                    matchers: [
                        [Node.ELEMENT_NODE, function(node: any, delta: any) {
                            var attrs: any = {};
                            if (node.classList) {
                                node.classList.forEach(function(c: string) {
                                    if (c.startsWith('ql-font-')) attrs.font = c.replace('ql-font-', '');
                                });
                            }
                            if (node.style && node.style.fontFamily) {
                                var font = node.style.fontFamily
                                    .replace(/['"]/g, '')
                                    .toLowerCase()
                                    .trim()
                                    .replace(/\s+/g, '-');
                                if (!attrs.font) attrs.font = font;
                            }
                            if (Object.keys(attrs).length > 0) {
                                delta.ops.forEach(function(op: any) {
                                    if (typeof op.insert === 'string') {
                                        op.attributes = Object.assign({}, op.attributes, attrs);
                                    }
                                });
                            }
                            return delta;
                        }]
                    ]
                },
                imageResize: false
            }
        });

        const removeImagesAndVideos = () => {
            const editor = quillRef.current;
            const editorContent = editor.getContents();
            let modified = false;

            editorContent.ops.forEach((op: any, index: number) => {
                if (op.insert && (op.insert.image || op.insert.video)) {
                    delete editorContent.ops[index].insert.image;
                    delete editorContent.ops[index].insert.video;
                    modified = true;
                }
            });
            if (modified) {
                editor.setContents(editorContent);
            }
        };

        if (quillRef.current) {
            const editor = quillRef.current.root;
            editor.style.fontWeight = 'normal'; 
            editor.style.fontFamily = 'Arial, sans-serif'; 
            editor.style.fontSize = '14px'; 
        }

        quillRef.current.on('text-change', () => {
            removeImagesAndVideos();
            props.onChange(quillRef.current.root.innerHTML);
        });
    }, []);

    useEffect(() => {
        if (quillRef.current && props.value != null) {
            const editorHtml = quillRef.current.root.innerHTML;

            
            if (editorHtml !== props.value) {
                quillRef.current.clipboard.dangerouslyPasteHTML(convertHtmlToQuillFormat(props.value));
            }
        }
    }, [props.value]);

    const undo = () => {
        quillRef.current.history.undo();
    };
    
    const redo = () => {
        quillRef.current.history.redo();
    };

    function convertHtmlToQuillFormat(html: string): string {
        if (!html) return '';
        const sizeMap: { [key: string]: string } = { '1': 'small', '3': 'normal', '5': 'large' };
        html = html.replace(/(<li>)?<font([^>]*)>([\s\S]*?)<\/font>(<\/li>)?/gi, (match, openLi, attrs, content) => {
            var cls = '';
            var inlineStyle = '';
            var size = (attrs.match(/size="(\d)"/) || [])[1];
            var face = (attrs.match(/face="([^"]+)"/) || [])[1];
            var color = (attrs.match(/color="([^"]+)"/) || [])[1];
            if (size && sizeMap[size]) cls += 'ql-size-' + sizeMap[size] + ' ';
            if (face) cls += 'ql-font-' + face.toLowerCase().replace(/\s+/g, '-') + ' ';
            if (color) inlineStyle += 'color:' + color + ';';
            return openLi
                ? `<li><span class="${cls.trim()}" style="${inlineStyle}">${content}</span></li>`
                : `<span class="${cls.trim()}" style="${inlineStyle}">${content}</span>`;
        });
        return html;
    }
    
    return (
        <div style={props.styles}>
            <div ref={toolbarRef} style={props.styles.iconWrapper}>
                <div className='iconWrapper'>
                    <span className="ql-formats">
                       <select className="ql-font" title="Font">
                            <option value="arial">Arial</option>
                            <option value="courier">Courier</option>
                            <option value="comic-sans-ms">Comic Sans MS</option>
                            <option value="helvetica">Helvetica</option>
                            <option value="open-sans">Open Sans</option>
                            <option value="tahoma">Tahoma</option>
                            <option value="verdana">Verdana</option>
                            <option value="sans-serif">Sans Serif</option>
                            <option value="serif">Serif</option>
                            <option value="monospace">Monospace</option>
                        </select>
                        <select className="ql-size" title="Font Size">
                            <option value="small">Small</option>
                            <option value="normal" selected>Normal</option>
                            <option value="large">Large</option>
                        </select>
                    </span>
                    <span className="ql-formats">
                        <button className="ql-bold" title="Bold"></button>
                        <button className="ql-italic" title="Italic"></button>
                        <button className="ql-underline" title="Underline"></button>
                        <button className="ql-strike" title="Strike"></button>
                    </span>
                    <span className="ql-formats">
                        <select className="ql-color" title="Text Color"></select>
                        <select className="ql-background" title="Background Color"></select>
                    </span>      
                    <span className="ql-formats">
                        <button className="ql-code-block" title="Code Block"></button>
                    </span>
                    <span className="ql-formats">
                        <button className="ql-list" value="ordered" title="Ordered List"></button>
                        <button className="ql-list" value="bullet" title="Bullet List"></button>
                        <button className="ql-indent" value="-1" title="Indent -1"></button>
                        <button className="ql-indent" value="+1" title="Indent +1"></button>
                    </span>
                    <span className="ql-formats">
                        <select className="ql-align" title="Align Text"></select>
                    </span>
                    <span className="ql-formats">
                        <button className="ql-link" title="Insert Link"></button>
                    </span>
                    <span className="ql-formats">
                        <button className="ql-undo" title="Undo"><Undo /></button>
                        <button className="ql-redo" title="Redo"><Redo /></button>
                    </span>
                </div>
            </div>
            <div ref={editorRef}></div>
        </div>
    );
}